import React from "react";
import { observer } from "mobx-react";
import { observable } from "mobx";
import InfiniteScroll from "react-infinite-scroll-component";

import { Button, Checkbox, Typography } from "@material-ui/core";

import ContentStudioMovementsStore from "stores/ContentStudioMovementsStore";

import Row from "components/Form/Row";
import Search from "components/Input/Search";
import Loader from "components/Layout/Loader";
import { SNACKBAR_TYPES } from "components/Snackbar";
import Placeholder from "components/Placeholder";
import SortingControl from "components/SortingControl";
import TrimmedText from "components/TrimmedText";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { DictionaryItem } from "constants/models";
import { TableSortType } from "stores/templates/TableStore";

import {
  Container,
  Buttons,
  ButtonContainer,
  SearchControlWrapper,
  SearchControls,
  MovementsContainer,
  MovementWrapper,
  MovementInfo,
  MovementText,
  MovementImage,
  ScrollLoader,
  TitleContainer,
  SubtitleContainer,
} from "./styles";

type Props = {
  options: DictionaryItem[];
  onClose: () => void;
  onSave: (newMovement: DictionaryItem[]) => void;
} & SnackbarContextProps;

const FORM_INFORMATION_HEIGHT = "450px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class MovementForm extends React.Component<Props> {
  @observable newMovement: DictionaryItem[] = [];

  componentDidMount() {
    ContentStudioMovementsStore.fetch();
  }

  componentWillUnmount() {
    ContentStudioMovementsStore.resetPage();
    ContentStudioMovementsStore.clearSearch();
  }

  fetchMoreMovements = async () => {
    const { setMessage } = this.props;

    try {
      await ContentStudioMovementsStore.fetchMoreMovements();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onChangeCheckbox = (option: DictionaryItem) => () => {
    let items = this.newMovement || [];

    if (items.find(item => item.value.id === option.value.id)) {
      items = items.filter(
        (item: DictionaryItem) => item.value.id !== option.value.id,
      );
    } else {
      items.push(option);
    }

    this.newMovement = items;
  };

  onSaveNewMovement = () => {
    this.props.onSave(this.newMovement);
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    ContentStudioMovementsStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      ContentStudioMovementsStore.fetch();
  };

  onSearchClear = () => {
    ContentStudioMovementsStore.clearSearch();
    ContentStudioMovementsStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    ContentStudioMovementsStore.applyOrder(value);
    ContentStudioMovementsStore.resetPage();
    ContentStudioMovementsStore.fetch();
  };

  isOptionChecked = (option: DictionaryItem) =>
    !!this.newMovement.find(movement => movement.value.id === option.value.id);

  render() {
    const { onClose, options } = this.props;

    return (
      <>
        <Container>
          <Row>
            <TitleContainer>
              <Typography variant="h1" color="textPrimary">
                Add Movements
              </Typography>
              {!!this.newMovement.length && (
                <SubtitleContainer>
                  <Typography variant="subtitle1" color="textPrimary">
                    {`${this.newMovement.length} items selected`}
                  </Typography>
                </SubtitleContainer>
              )}
            </TitleContainer>
          </Row>

          <Row>
            <Search
              initValue={ContentStudioMovementsStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={
                    ContentStudioMovementsStore.orderContext.appliedOrderInfo
                  }
                  onApply={this.onSortApply}
                  disablePortal
                />
              </SearchControlWrapper>
            </SearchControls>
          </Row>

          <Row>
            {!ContentStudioMovementsStore.isLoading ? (
              <MovementsContainer>
                {options.length ? (
                  <InfiniteScroll
                    dataLength={options.length}
                    hasMore={ContentStudioMovementsStore.hasMoreInfo}
                    next={this.fetchMoreMovements}
                    height={`calc(100vh - ${FORM_INFORMATION_HEIGHT})`}
                    loader={<ScrollLoader />}
                  >
                    {options.map(option => (
                      <MovementWrapper
                        key={option.value.id}
                        checked={this.isOptionChecked(option)}
                      >
                        <MovementInfo>
                          <MovementImage image={option.value.imageUrl} />
                          <MovementText>
                            <TrimmedText lines={1} text={option.label}>
                              <Typography variant="h4">
                                {option.label}
                              </Typography>
                            </TrimmedText>
                            <Typography variant="subtitle1">
                              {option.value.durationText}
                            </Typography>
                          </MovementText>
                        </MovementInfo>
                        <Checkbox
                          color="primary"
                          onClick={this.onChangeCheckbox(option)}
                          checked={this.isOptionChecked(option)}
                        />
                      </MovementWrapper>
                    ))}
                  </InfiniteScroll>
                ) : (
                  <Placeholder
                    height={132}
                    width={132}
                    text={
                      ContentStudioMovementsStore.search
                        ? "No matches found. Please try another search."
                        : "No movements added yet."
                    }
                  />
                )}
              </MovementsContainer>
            ) : (
              <Loader />
            )}
          </Row>
        </Container>

        <Buttons>
          <ButtonContainer>
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
            >
              Cancel
            </Button>
          </ButtonContainer>
          <ButtonContainer>
            <Button
              variant="contained"
              color="primary"
              onClick={this.onSaveNewMovement}
              fullWidth
            >
              Save
            </Button>
          </ButtonContainer>
        </Buttons>
      </>
    );
  }
}

export default withGlobalSnackbar(MovementForm);
